import { useCallback, useMemo, useRef } from 'react'
import { EmbeddedMessageAttachment, RiverTimelineEvent, TimelineEvent } from '@towns-protocol/sdk'
import { isEqual, uniqBy } from 'lodash-es'
import { notUndefined } from 'ui/utils/utils'
import { useTownsContext, useUserLookupStore } from 'use-towns-client'
import { isTimelineEventWithChannelMessageContent } from 'utils/isTimelineEventWithChannelMessageContent'

import { useExtractInternalLinks } from './useExtractInternalLinks'

export const useExtractMessageAttachments = (
    text: string,
): { attachments: EmbeddedMessageAttachment[] | undefined } => {
    const links = useExtractInternalLinks(text)
    const ref = useRef<EmbeddedMessageAttachment[]>(undefined)
    const { casablancaClient } = useTownsContext()

    const { spaces } = useTownsContext()

    const { getChannelEvent } = useGetChannelEvent()

    const attachments = useMemo(() => {
        if (!casablancaClient) {
            return undefined
        }
        const attachments = links
            ?.map((link) => {
                const { url, spaceId, channelId, messageId } = link
                const event = getChannelEvent(channelId, messageId)
                if (!event) {
                    return
                }

                const userId = event.sender.id
                const spaceName = spaces.find((s) => s.streamId === spaceId)?.name ?? ''

                return createEmbeddedMessageAttachment({
                    url,
                    spaceId,
                    channelId,
                    messageId,
                    userId,
                    event,
                    staticInfo: createStaticInfo({ spaceId, spaceName, channelId, userId }),
                })
            })
            .filter(notUndefined)

        const result = attachments ? uniqBy(attachments, 'url') : undefined

        if (!isEqual(result, ref.current)) {
            ref.current = result
        }

        return ref.current
    }, [casablancaClient, getChannelEvent, links, spaces])

    return { attachments }
}

type CreateEmbeddedMessageAttachmentParams = {
    url: string
    spaceId: string
    channelId: string
    messageId: string
    userId: string
    event: TimelineEvent
    staticInfo?: ReturnType<typeof createStaticInfo>
}

export const useGetChannelEvent = () => {
    const { casablancaClient } = useTownsContext()
    const getChannelEvent = useCallback(
        (channelId: string, eventId: string) => {
            if (!casablancaClient) {
                return undefined
            }
            const event = casablancaClient.streamsView.timelinesView.value.timelines[
                channelId
            ]?.find((e) => e.eventId === eventId)
            if (event?.content?.kind !== RiverTimelineEvent.ChannelMessage) {
                return undefined
            }
            return event
        },
        [casablancaClient],
    )

    return { getChannelEvent }
}

export const createEmbeddedMessageAttachment = ({
    url,
    spaceId,
    channelId,
    messageId,
    userId,
    event,
    staticInfo,
}: CreateEmbeddedMessageAttachmentParams): EmbeddedMessageAttachment => {
    const channelMessageEvent = isTimelineEventWithChannelMessageContent(event) ? event : undefined

    if (!channelMessageEvent) {
        throw new Error('Content is not a text message')
    }

    const channelMessageEventContent = {
        ...channelMessageEvent.content,
        attachments: channelMessageEvent.content.attachments?.filter(
            (attachment) => attachment.type !== 'embedded_message',
        ),
    }

    return {
        type: 'embedded_message' as const,
        id: `${spaceId}${channelId}${messageId}`,
        url,
        channelMessageEvent: channelMessageEventContent,
        info: {
            createdAtEpochMs: BigInt(event.createdAtEpochMs),
            userId,
            spaceId,
            channelId,
            messageId,
        },
        staticInfo,
    } satisfies EmbeddedMessageAttachment
}

export const createStaticInfo = ({
    spaceId,
    userId,
    spaceName,
}: {
    spaceId: string
    spaceName: string
    channelId: string
    userId: string
}) => {
    const spaceUser = useUserLookupStore.getState().spaceUsers[spaceId]?.[userId]
    const user = spaceId ? spaceUser : undefined

    return {
        spaceName,
        userName: user?.username,
        displayName: user?.displayName,
        ensName: user?.ensName,
    }
}
